import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { useMutation } from '@tanstack/react-query';
import {
  Upload,
  Loader2,
  AlertCircle,
  CheckCircle,
  File,
  FileSpreadsheet,
} from 'lucide-react';
import api, { extractError } from '../lib/api';

export default function ImportPage() {
  const { user } = useAuth();
  const orgId = user?.organizationId || '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  useEffect(() => {
    api.get('/import-configs', { params: { limit: 100 } }).then(res => {
      // Only show active CSV/EXCEL/JSON/XML configurations
      const filtered = (res.data || []).filter((c: {sourceType:string,isActive:boolean}) =>
        ['CSV','EXCEL','JSON','XML'].includes(c.sourceType) && c.isActive
      );
      setImportConfigs(filtered);
    }).catch(() => {});
  }, []);

  const downloadTemplate = async (type: string) => {
    try {
      const stored = localStorage.getItem('rotkreuz-auth');
      if (!stored) return;
      const parsed = JSON.parse(stored);
      const token = parsed.state?.token;
      if (!token) return;
      const res = await fetch('/api/v1/imports/templates/' + type, {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type + '.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Template download failed', e);
    }
  };

  const [importType, setImportType] = useState<'organizations' | 'fields' | 'entries'>('entries');
  const [importConfigId, setImportConfigId] = useState('');
  const [importConfigs, setImportConfigs] = useState<Array<{id:string;name:string;sourceType:string}>>([]);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (importType === 'fields' && orgId) formData.append('organizationId', orgId);
      const res = await api.post(`/imports/${importType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      setResult({ success: true, message: data.message ?? 'Import erfolgreich' });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    onError: (err) => {
      setResult({ success: false, message: extractError(err).message });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    if (importConfigId) formData.append('importConfigId', importConfigId);
    mutation.mutate(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setResult(null);
  };

  const acceptedFormats = '.csv,.xlsx,.xls,.json';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Upload size={28} className="text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import</h1>
          <p className="text-sm text-gray-500">Daten importieren (CSV, Excel, JSON)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Import konfigurieren</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="import-type" className="label">Import-Typ</label>
              <select
                id="import-type"
                className="input-field"
                value={importType}
                onChange={(e) => { setImportType(e.target.value as typeof importType); setResult(null); }}
              >
                <option value="organizations">Organisationen</option>
                <option value="fields">Datenfelder</option>
                <option value="entries">Dateneinträge</option>
              </select>
            </div>

            {importType === 'entries' && (
              <div>
                <label htmlFor="import-config" className="label">Import-Konfiguration (optional)</label>
                <select
                  id="import-config"
                  className="input-field"
                  value={importConfigId}
                  onChange={(e) => setImportConfigId(e.target.value)}
                >
                  <option value="">— Ohne Config (einfacher Import) —</option>
                  {importConfigs.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.sourceType})</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Mit einer Import-Konfiguration werden Spalten automatisch zu Datenfeldern gemappt
                </p>
              </div>
            )}

            <div>
              <label className="label">Datei auswählen</label>
              <div className="mt-1 flex justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-8 hover:border-primary transition-colors">
                <div className="text-center">
                  {selectedFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet size={40} className="text-accent-green-dark" />
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                      <button
                        type="button"
                        onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Entfernen
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer"
                    >
                      <Upload size={40} className="mx-auto text-gray-300" />
                      <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium text-primary">Datei auswählen</span>
                        {' '}oder hierher ziehen
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {acceptedFormats.toUpperCase()}
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    id="file-upload"
                    type="file"
                    accept={acceptedFormats}
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={!selectedFile || mutation.isPending}
              className="btn-primary w-full"
            >
              {mutation.isPending ? (
                <><Loader2 size={18} className="animate-spin" /> Importiere...</>
              ) : (
                <><Upload size={18} /> Import starten</>
              )}
            </button>
          </form>
        </div>

        <div className="space-y-4">
          {/* Result */}
          {result && (
            <div className={`card border-2 ${
              result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
            }`}>
              <div className="flex items-center gap-3">
                {result.success ? (
                  <CheckCircle size={24} className="text-green-600 shrink-0" />
                ) : (
                  <AlertCircle size={24} className="text-red-600 shrink-0" />
                )}
                <p className={`text-sm font-medium ${
                  result.success ? 'text-green-800' : 'text-red-800'
                }`}>{result.message}</p>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-2">Hinweise</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <File size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>Unterstützte Formate: CSV, Excel (.xlsx, .xls) und JSON</span>
              </li>
              <li className="flex items-start gap-2">
                <File size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>CSV-Dateien müssen UTF-8 kodiert sein</span>
              </li>
              <li className="flex items-start gap-2">
                <File size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>Die erste Zeile muss die Spaltenüberschriften enthalten</span>
              </li>
              <li className="flex items-start gap-2">
                <File size={14} className="mt-0.5 shrink-0 text-primary" />
                <span>Maximale Dateigröße: 50 MB</span>
              </li>
            </ul>
          </div>

          <div className="card bg-accent-turquoise-light/10 border-accent-turquoise-light/30">
            <h3 className="font-semibold text-gray-900 mb-3">Vorlagen zum Download</h3>
            <div className="space-y-2 text-sm">
              <button onClick={() => downloadTemplate("organizations")} className="flex items-center gap-2 text-primary hover:text-primary-dark hover:underline cursor-pointer bg-transparent border-none text-sm p-0">
                <FileSpreadsheet size={16} /> Organisationen importieren (CSV)
              </button>
              <button onClick={() => downloadTemplate("fields")} className="flex items-center gap-2 text-primary hover:text-primary-dark hover:underline cursor-pointer bg-transparent border-none text-sm p-0">
                <FileSpreadsheet size={16} /> Datenfelder importieren (CSV)
              </button>
              <button onClick={() => downloadTemplate("entries")} className="flex items-center gap-2 text-primary hover:text-primary-dark hover:underline cursor-pointer bg-transparent border-none text-sm p-0">
                <FileSpreadsheet size={16} /> Dateneinträge importieren (CSV)
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Die CSV-Vorlagen enthalten die korrekte Spaltenstruktur. Lade sie herunter, befüll sie mit deinen Daten und importiere sie oben.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
